import type { IObservableArray } from 'mobx';
import { autorun, observable, set } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPersonCustomData, TPersonCustomDataValue } from '@feathr/blackbox';
import type { CustomField, Person } from '@feathr/blackbox';
import { Button, Fieldset } from '@feathr/components';
import { StoresContext } from '@feathr/extender/state';
import { moment, TimeFormat, useToggle } from '@feathr/hooks';

import PersonAddCustomData from './PersonAddCustomData';
import PersonCustomDataInputs from './PersonCustomDataInputs';

import * as styles from './PersonCustomData.css';

export interface ICustomDataObject {
  key: string;
  value: TPersonCustomDataValue;
}

interface IProps {
  person: Person;
}

function PersonCustomData({ person }: IProps): JSX.Element {
  const { CustomFields } = useContext(StoresContext);
  const [toggleAddData, setToggleAddData] = useToggle(false);
  const { t } = useTranslation();

  const customData = observable.array(
    Object.entries(person.get('custom_data', {})).map(([key, value]) => ({ key, value })),
  ) as IObservableArray<ICustomDataObject>;

  function handleValueChange(datumKey: string, newValue: TPersonCustomDataValue): void {
    set(customData[customData.findIndex(({ key }) => key === datumKey)], {
      value: newValue,
    });
  }

  useEffect(
    () =>
      autorun(() => {
        const newCustomData = customData.reduce((ncd, { key, value }) => {
          ncd[key] = value instanceof Date ? moment.utc(value).format(TimeFormat.isoDate) : value;
          return ncd;
        }, {} as IPersonCustomData);
        person.set({ custom_data: newCustomData });
      }),
    [customData, person],
  );

  async function addCustomField(field: CustomField): Promise<void> {
    await field.save();
    await CustomFields.add(field);
    person.set({
      custom_data: { ...person.get('custom_data', {}), [field.get('u_key')]: undefined },
    });
  }

  return (
    <Fieldset label={t('Custom Fields')}>
      {customData
        ? customData.map(({ key, value }) => (
            <div id={key} key={key}>
              <PersonCustomDataInputs
                addCustomField={addCustomField}
                customData={customData}
                datumKey={key}
                handleValueChange={handleValueChange}
                value={value}
              />
            </div>
          ))
        : null}
      {!toggleAddData ? (
        <Button
          className={styles.button}
          key={'add-custom-data'}
          name={'add-custom-data-button'}
          onClick={setToggleAddData}
          title={t('Add Custom Data')}
          type={'primary'}
        >
          {t('Add custom field')}
        </Button>
      ) : (
        <PersonAddCustomData person={person} toggleAddData={setToggleAddData} />
      )}
    </Fieldset>
  );
}

export default observer(PersonCustomData);
